import React, { useEffect, useState } from 'react';
import { BaseButton } from '../Controls/Button/BaseButton';
import './css/NumberStepper.css';

export interface NumberStepperProps {
    value: number | null;
    onChange: (value: number | null) => void;

    min?: number;
    max?: number;
    step?: number;
    buttonLayout?: 'row' | 'column';
    disabled?: boolean;
    className?: string;
}

const round1dp = (value: number): number =>
    Math.round(value * 10) / 10;

const clamp = (value: number, min: number, max: number): number =>
    Math.min(max, Math.max(min, value));

export const NumberStepper: React.FC<NumberStepperProps> = ({
    value,
    onChange,
    min = 0,
    max = 50,
    step = 0.5,
    buttonLayout = "row",
    disabled = false,
    className = ''
}) => {
    const [textValue, setTextValue] = useState<string>('');

    const [addDisabled, setAddDisabled] = useState<boolean>(disabled);
    const [subtractDisabled, setSubtractDisabled] = useState<boolean>(disabled);

    useEffect(() => {
        setTextValue(value === null ? '' : value.toFixed(1));

        if (!disabled && value != null && min != undefined && max != undefined) {
            setAddDisabled(value === max);
            setSubtractDisabled(value === min);
        }
    }, [value]);

    const commitValue = (rawValue: number): void => {
        const rounded = round1dp(rawValue);
        const clamped = clamp(rounded, min, max);

        if (!disabled && clamped != null && min != undefined && max != undefined) {
            setAddDisabled(clamped === max);
            setSubtractDisabled(clamped === min);
        }

        setTextValue(clamped.toFixed(1));
        onChange(clamped);
    };

    const handleTextChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ): void => {
        const text = event.target.value;

        setTextValue(text);

        if (text.trim() === '') {
            onChange(null);
        }
    };

    const handleKeyDown = (
        event: React.KeyboardEvent<HTMLInputElement>
    ): void => {
        if (event.key === 'Enter') {
            event.currentTarget.blur();
        }
    };

    const handleBlur = (): void => {
        const text = textValue.trim();

        if (text === '') {
            setTextValue('');
            return;
        }

        const parsed = Number(text);

        if (Number.isNaN(parsed)) {
            setTextValue(value === null ? '' : value.toFixed(1));
            return;
        }

        commitValue(parsed);
    };

    const increment = (): void => {
        if (disabled) {
            return;
        }

        if (value === null) {
            commitValue(min);
            return;
        }

        commitValue(value + step);
    };

    const decrement = (): void => {
        if (disabled) {
            return;
        }

        if (value === null) {
            commitValue(min);
            return;
        }

        commitValue(value - step);
    };

    return (
        <div className={`number-stepper ${className}`.trim()}>
            <input
                type="text"
                value={textValue}
                disabled={disabled}
                onChange={handleTextChange}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                className="number-stepper-input"
            />

            <div className={`number-stepper-buttons number-stepper-buttons-${buttonLayout}`}>

                <BaseButton ariaLabel="number-stepper-inc"
                    className="number-stepper-button"
                    onClick={increment}
                    disabled={addDisabled}>
                    <div className="number-stepper-inner">+</div>
                </BaseButton>
                <BaseButton
                    ariaLabel="number-stepper-inc"
                    className="number-stepper-button"
                    onClick={decrement}
                    disabled={subtractDisabled}>
                    <div className="number-stepper-inner">-</div>
                </BaseButton>
            </div>
        </div>
    );
};

export default NumberStepper;
